import { CardTemplateReader, renderTemplateNodes } from '@gladly/card-renderer';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import _ from 'lodash';

import { applyArrayLimits } from 'components/customer/external_app_card/lib/apply_array_limits';
import { applyArraySorts } from 'components/customer/external_app_card/lib/apply_array_sorts';
import { getCardRendererHostConfig } from 'components/lib/card_renderer_host_config';
import connect from 'components/lib/connect';
import ExpandableProfileCard from 'components/customer/profile/expandable_profile_card';
import ExternalAppCardData from 'models/external_apps/external_app_card_data';
import ExternalAppCardTemplate from 'models/external_apps/external_app_card_template';
import { getCurrentCustomerState } from 'components/customer/summary/lib/store_helpers';
import ProfileCard from 'components/customer/profile/profile_card';
import ProfileCardDef from 'models/configuration/profile_card_def';
import ProfileErrorCard from 'components/customer/profile/profile_error_card';
import qconsole from 'scripts/lib/qconsole';
import SendAppCardRenderAnalytics from 'actions/external_apps/send_app_card_render_analytics';
import SendErrorCardRenderAnalytics from 'actions/external_apps/send_error_card_render_analytics';
import Spinner from 'components/common/spinner_two';
import { useOnUnmount } from 'components/hooks/lifecycle_hooks';

const DEFAULT_EXPAND_THRESHOLD = 5;

export function ExternalAppCardBase(props) {
  const {
    cardConfiguration,
    cardTemplate,
    data,
    isLoading,
    isVisible,
    hasErrors,
    onAppCardRenderedEvent,
    onErrorCardRenderedEvent,
  } = props;
  const rendererState = useRef({});
  useOnUnmount(() => {
    rendererState.current = {};
  });

  // Quick sanity check on the card configuration
  const appNamespace = _.trim(_.get(cardConfiguration, 'appNamespace'));
  const cardId = _.trim(_.get(cardConfiguration, 'externalAppCardId'));
  const rootDataAttr = _.trim(_.get(cardConfiguration, 'templateData.queryName'));
  const limits = _.get(cardConfiguration, 'templateData.responseTransforms.limit');
  const sorts = _.get(cardConfiguration, 'templateData.responseTransforms.sort');
  if (!appNamespace || !cardId || !rootDataAttr) {
    qconsole.debug(
      `ExternalAppCard configuration error. App namespace: "${appNamespace}", card ID: "${cardId}", query name: "${rootDataAttr}"`
    );
    onErrorCardRenderedEvent({ reason: 'Misconfiguration' });
    return (
      <ProfileErrorCard
        data-aid="external-app-card-configuration-error"
        reason="The card configuration is incorrect."
      />
    );
  }

  // Check if we need to hide (or if there were loading errors)
  if (!isVisible || (_.isNil(data) && !isLoading && !hasErrors)) return null;
  if (hasErrors) {
    onErrorCardRenderedEvent({ reason: 'Loading errors' });
    return (
      <ProfileErrorCard
        data-aid={`external-app-card-loading-error-${appNamespace}-${cardId}`}
        reason="Unable to load required data."
      />
    );
  }

  // Are we still waiting for the data? Display the spinner and quit
  const dataAid = `external-app-card-${appNamespace}-${cardId}`;
  if (isLoading) {
    return (
      <ProfileCard data-aid={dataAid} isEmpty isLoading>
        <Spinner key="loading-spinner" />
      </ProfileCard>
    );
  }

  // If we are here, we need to render the template. Get the data nice and ready first.
  const transformedData = transformDataForDisplay(cardId, data, limits, sorts);
  const templateData = _.get(transformedData, rootDataAttr);
  if (_.isNil(templateData)) return null;

  // Now try getting the card title and other template meta
  let expanderLimit = DEFAULT_EXPAND_THRESHOLD;
  let cardTitle = '';
  let renderedNodes = [];
  let isExpandable = true;
  try {
    const reader = new CardTemplateReader({ preprocessTemplate: false });
    const { title, expandThreshold, cardType } = reader.extractCardParameters(cardTemplate, templateData);
    const threshold = Number(expandThreshold);

    cardTitle = _.trim(title);
    isExpandable = _.trim(cardType).toLowerCase() !== ExternalAppCardTemplate.CardTypes.STATIC;
    expanderLimit = _.isNaN(threshold) || threshold <= 0 ? DEFAULT_EXPAND_THRESHOLD : threshold;
    renderedNodes = renderTemplateNodes({
      template: cardTemplate,
      data: templateData,
      hostConfig: getCardRendererHostConfig(),
      options: { preprocessTemplate: false },
      stateStore: rendererState.current,
    });

    // Do we have anything to render? In not, do not display the card
    if (_.isEmpty(renderedNodes)) return null;
  } catch (err) {
    qconsole.debug('ExternalAppCard rendering error', err.message);
    onErrorCardRenderedEvent({ reason: 'Rendering error', error: err.message });
    return (
      <ProfileErrorCard
        data-aid="external-app-card-configuration-error"
        reason="The card configuration is incorrect."
      />
    );
  }

  onAppCardRenderedEvent({ appNamespace });
  return isExpandable ? (
    <ExpandableProfileCard data-aid={dataAid} limit={expanderLimit || undefined} title={cardTitle}>
      {renderedNodes}
    </ExpandableProfileCard>
  ) : (
    <ProfileCard data-aid={dataAid} title={cardTitle}>
      {renderedNodes}
    </ProfileCard>
  );
}

ExternalAppCardBase.propTypes = {
  cardConfiguration: PropTypes.object.isRequired,
  cardTemplate: PropTypes.string,
  customerId: PropTypes.string,
  data: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool, PropTypes.object, PropTypes.array]),
  hasErrors: PropTypes.bool,
  isLoading: PropTypes.bool,
  isVisible: PropTypes.bool,
  onAppCardRenderedEvent: PropTypes.func.isRequired,
  onErrorCardRenderedEvent: PropTypes.func.isRequired,
};

function mapStateToProps({ getProvider }, { profileCardDef }) {
  const { customerId, isCustomerLoaded } = getCurrentCustomerState(getProvider);
  const dataStoreProvider = getProvider('externalAppCardData');
  const templateProvider = getProvider('externalAppCardTemplates');

  // Get the card configuration parameters
  const cardConfiguration = _.get(profileCardDef, 'properties.cardParameters') || {};

  // Are we on a completely loaded customer profile?
  if (!customerId || !isCustomerLoaded) {
    // Hide the card until the customer profile is loaded, we know the `customerId`, and
    // the customers store is in place
    return {
      cardConfiguration,
      isVisible: false,
    };
  }

  const appCardId = _.trim(cardConfiguration.externalAppCardId);
  if (!appCardId) {
    // Check for [unlikely] misconfiguration
    return {
      cardConfiguration,
      customerId,
      hasErrors: true,
      isVisible: true,
    };
  }

  const isLoading = templateProvider.isLoading() || dataStoreProvider.isLoading({ requester: appCardId });
  const isRequestError =
    !_.isEmpty(templateProvider.getErrorForLoading()) ||
    !_.isEmpty(dataStoreProvider.getErrorForLoading({ requester: appCardId }));
  const cardTemplateInfo = getCardTemplateInfo(templateProvider, appCardId);
  const dataEnvelope = getCardDataEnvelope(dataStoreProvider, appCardId);

  // If the store is still loading, `cardTemplate` will be an empty string
  const cardTemplate = _.trim(cardTemplateInfo?.template);

  // Translate the errors that we might have received while trying to load the data
  const dataErrors = dataEnvelope?.errors || [];
  const platformErrorCodes = dataErrors
    .map(err => _.trim(err.meta?.appPlatformErrorCode).toUpperCase())
    .filter(code => !!code);
  const nonIgnorablePlatformErrors = _.difference(platformErrorCodes, ExternalAppCardData.ErrorCodes.Ignorable);
  const hasNonIgnorableErrors = dataErrors.length > platformErrorCodes.length || !!nonIgnorablePlatformErrors.length;

  const hasErrors = isRequestError || !_.isEmpty(cardTemplateInfo?.errors) || hasNonIgnorableErrors;
  const isVisible = !templateProvider.isLoading() && !!cardTemplate && (!dataErrors.length || hasNonIgnorableErrors);

  return {
    cardConfiguration,
    cardTemplate,
    customerId,
    data: dataEnvelope?.data,
    hasErrors,
    isLoading,
    isVisible,
  };
}

function mapExecuteToProps(executeAction) {
  return {
    onAppCardRenderedEvent: eventData => executeAction(SendAppCardRenderAnalytics, eventData),
    onErrorCardRenderedEvent: eventData => executeAction(SendErrorCardRenderAnalytics, eventData),
  };
}

// ==================================== HELPERS =================================

function getCardTemplateInfo(storeProvider, cardId) {
  if (storeProvider.isLoading()) return;

  const items = storeProvider.findAll();
  return _.find(items, templateInfo => templateInfo.externalAppCardId === cardId);
}

function getCardDataEnvelope(storeProvider, cardId) {
  if (storeProvider.isLoading({ requester: cardId })) return;

  const items = storeProvider.findAll();
  return _.find(items, envelope => envelope.externalAppCardId === cardId);
}

function transformDataForDisplay(cardId, data, limits, sorts) {
  try {
    const sorted = !_.isEmpty(sorts) ? applyArraySorts(data, sorts) : data;
    return !_.isEmpty(limits) ? applyArrayLimits(sorted, limits) : sorted;
  } catch (err) {
    qconsole.error(
      `ExternalAppCard: possible misconfiguration for card ${cardId}. Error while trying to apply data transforms: ${err.message}`
    );
    throw err;
  }
}

// ================================= CONNECTED COMPONENT ========================

const ConnectedExternalAppCard = connect(mapStateToProps, mapExecuteToProps)(ExternalAppCardBase);
ConnectedExternalAppCard.propTypes = {
  profileCardDef: PropTypes.instanceOf(ProfileCardDef).isRequired,
};

export default ConnectedExternalAppCard;
